import { GQLProductSideAvailableDecorationType, GQLProductSideSideType } from '@theprintbar/graphql';
import z from 'zod';
import { zfd } from 'zod-form-data';
import { fileSchema, fileServerSchema } from '../../schemas';
import { booleanField } from '../../zod';

export const emptyDecorationMeta: DecorationMeta = {
	width: 0,
	height: 0,
	centreOffset: 0,
	left: 0,
	top: 0,
	exportDPI: 0,
	percentages: {
		width: 0,
		height: 0,
		left: 0,
		centreOffset: 0,
		top: 0,
		bottom: 0,
		right: 0,
	},
	colours: [],
};

export const positionSchema = z.object({
	width: zfd.numeric(),
	height: zfd.numeric(),
	left: zfd.numeric(),
	centreOffset: zfd.numeric(),
	top: zfd.numeric(),
	bottom: zfd.numeric().optional(),
	right: zfd.numeric().optional(),
	exportDPI: zfd.numeric().optional(),
});

export const pantoneSchema = z.object({
	name: zfd.text(),
	r: zfd.numeric(),
	g: zfd.numeric(),
	b: zfd.numeric(),
});

export const colourMapSchema = z.object({
	oldColour: pantoneSchema,
	newColour: pantoneSchema,
});

export type ColourMap = z.infer<typeof colourMapSchema>;

export const graphicDataSchema = z.object({
	block: z.coerce.number(),
	designId: z.string().optional(),
	dpi: z.coerce.number().optional(),
	localImage: booleanField(),
	hasCutoff: booleanField(),
	manipulated: booleanField(),
	fileSize: zfd.numeric().optional(),
	fileWidth: zfd.numeric().optional(),
	fileHeight: zfd.numeric().optional(),
});

type SingleRawImageMeta = Pick<Decoration['meta'], 'graphicData' | 'sceneItems'>;

export function isSingleRawImage(meta: SingleRawImageMeta): boolean {
	if (!meta.graphicData?.length || meta.sceneItems !== 1) return false;
	const graphic = meta.graphicData[0];
	return !!graphic.designId && !graphic.manipulated;
}

export function getSingleRawImage(meta: SingleRawImageMeta): GraphicData | undefined {
	if (!isSingleRawImage(meta)) return undefined;
	return meta.graphicData![0];
}

// Crop rectangle in source-image pixel coordinates. Emitted by the client
// when it can't decode+crop a huge source at full DPI on-device (see
// cropSourceImageForOverlap in getArtwork.ts). The server pairs it with
// meta.graphicData[0] (which already carries the source's designId/localImage
// flag/dimensions) to persist a Design row whose image_* fields point at the
// source S3 asset and whose cropSx/Sy/Sw/Sh describe the extraction. The
// design-image endpoint runs sharp().extract(...) on demand; CDN caches by URL.
export const cropSpecSchema = z.object({
	sx: zfd.numeric(),
	sy: zfd.numeric(),
	sw: zfd.numeric(),
	sh: zfd.numeric(),
});

export type CropSpec = z.infer<typeof cropSpecSchema>;

export const decorationSchema = z.object({
	updatePrice: booleanField(),
	isMainDecoration: booleanField().optional(),
	side: zfd.text(z.nativeEnum(GQLProductSideSideType)),
	type: zfd.text(z.nativeEnum(GQLProductSideAvailableDecorationType)),
	meta: positionSchema.extend({
		sceneItems: z.coerce.number().optional(),
		percentages: positionSchema,
		requestedColours: z.coerce.number().optional(),
		colours: z.array(pantoneSchema).optional(),
		trueview: z.instanceof(Blob).optional(),
		stitches: zfd.numeric().optional(),
		useSuppliedEmbFile: booleanField(),
		graphicData: z.array(graphicDataSchema).optional(),
	}),
	attachment: fileSchema.optional(),
	artwork: z.instanceof(Blob).optional(),
	// PDF/X-4 print-ready output generated alongside the artwork by @imgly/plugin-print-ready-pdfs-web
	// during final export. Optional — falls back to the PNG artwork if the PDF/X conversion fails.
	printReady: z.instanceof(Blob).optional(),
	// Mutually exclusive with `artwork`: when set, the server should skip
	// artwork upload and instead persist this crop spec onto the Design row.
	cropSpec: cropSpecSchema.optional(),
	error: z
		.object({
			title: z.string(),
			message: z.string(),
		})
		.optional(),
});

export const decorationSchemaServer = decorationSchema.extend({
	artwork: fileServerSchema.optional(),
	attachment: fileServerSchema.optional(),
	printReady: fileServerSchema.optional(),
	meta: decorationSchema.shape.meta.extend({
		trueview: fileServerSchema.optional(),
	}),
});

export type Decoration = z.infer<typeof decorationSchema>;
export type DecorationServer = z.infer<typeof decorationSchemaServer>;

export type DecorationMeta = Decoration['meta'];
export type GraphicData = z.infer<typeof graphicDataSchema>;
export type Position = z.infer<typeof positionSchema>;
export type ArtworkAndMeta = Pick<Decoration, 'artwork' | 'meta' | 'printReady' | 'cropSpec'>;
